import React, { useEffect, useState } from 'react';
import { ArrowPathIcon, SignalIcon, SignalSlashIcon, TruckIcon } from '@heroicons/react/24/outline';
import api from '@/services/api/client';
import { useLanguage, useTranslation } from '@/store/languageStore';
import { isDesignPreviewHost } from '@/utils/designPreview';
import { fleetConnectionShare } from '@/utils/fleetNow';
import { uiDateLocale } from '@/utils/uiLocale';

const FLEET_REFRESH_MS = 6 * 60 * 1000;

interface VehicleOnlineSummary {
  totalCount: number;
  onlineCount: number;
  offlineCount: number;
  linkedCount?: number;
  unlinkedCount?: number;
}

function ConnectionDonut({ percent, caption }: { percent: number; caption: string }) {
  const size = 112;
  const stroke = 12;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const filled = (percent / 100) * circumference;

  return (
    <div className="relative h-36 w-36 shrink-0">
      <svg viewBox={`0 0 ${size} ${size}`} className="h-full w-full -rotate-90" aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          className="text-borderDefault"
        />
        {percent > 0 && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${filled} ${circumference - filled}`}
            className="text-teal-400"
          />
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-semibold tabular-nums text-textDefault">{percent}%</span>
        <span className="text-xs text-textSecondary">{caption}</span>
      </div>
    </div>
  );
}

const VehicleListWidget: React.FC = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const designPreview = typeof window !== 'undefined' && isDesignPreviewHost(window.location.hostname);
  const format = new Intl.NumberFormat(uiDateLocale(language));
  const [summary, setSummary] = useState<VehicleOnlineSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/dashboard/data/vehicle-online-summary');
      if (response.data.success) {
        setSummary(response.data.data);
        setError(null);
      } else {
        setError(response.data.error || 'Failed to load vehicles');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load vehicles';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchSummary();
    const timer = window.setInterval(() => { void fetchSummary(); }, FLEET_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, []);

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error && !summary) {
    return <div className="text-red-500 text-sm py-2">{error}</div>;
  }

  const totalCount = summary?.totalCount ?? 0;
  const onlineCount = summary?.onlineCount ?? 0;
  const offlineCount = summary?.offlineCount ?? 0;
  const linkedCount = summary?.linkedCount ?? 0;
  const share = fleetConnectionShare(linkedCount, totalCount);

  if (!designPreview) {
    return (
      <div className="flex flex-col">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <TruckIcon className="w-8 h-8 text-blue-500" />
            <div>
              <div className="text-2xl font-bold text-textDefault">{totalCount}</div>
              <div className="text-xs text-textSecondary">{t('vehicleListWidget.totalVehicles')}</div>
            </div>
          </div>
          <button
            onClick={() => void fetchSummary()}
            className="p-1 hover:bg-bgElevated rounded"
            title={t('common.refresh')}
          >
            <ArrowPathIcon className={`w-4 h-4 text-textSecondary ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-borderDefault">
          <div className="flex items-center gap-1.5 text-green-500">
            <SignalIcon className="w-4 h-4" />
            <span className="font-semibold">{onlineCount}</span>
            <span className="text-xs text-textTertiary">online</span>
          </div>
          <div className="flex items-center gap-1.5 text-textTertiary">
            <SignalSlashIcon className="w-4 h-4" />
            <span className="font-semibold">{offlineCount}</span>
            <span className="text-xs text-textTertiary">offline</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      <img
        src="/deviaq-logo.png"
        alt=""
        aria-hidden="true"
        draggable={false}
        className="pointer-events-none absolute max-w-none select-none"
        style={{ width: 400, height: 389, right: -189, bottom: -240 }}
      />
      <h2 className="relative text-base font-semibold text-textDefault">{t('vehicleListWidget.fleetNow')}</h2>
      <div className="relative mt-4 flex min-w-0 items-center gap-5">
        <ConnectionDonut percent={share} caption={t('vehicleListWidget.withDevice')} />
        <div className="min-w-0">
          <div className="text-4xl font-semibold tabular-nums leading-none text-textDefault">{format.format(totalCount)}</div>
          <div className="mt-1 text-sm text-textSecondary">{t('vehicleListWidget.totalVehicles')}</div>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-textDefault">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-teal-400" />
              <span className="font-semibold tabular-nums">{format.format(onlineCount)}</span>
              {t('vehicleListWidget.online')}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-textTertiary" />
              <span className="font-semibold tabular-nums">{format.format(offlineCount)}</span>
              {t('vehicleListWidget.offline')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleListWidget;
